import { Box, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useUpsertAssetToCurrentValuationWithEntityUpdatesMutation } from '@/modules/assetValuation/graphql/mutations.generated';
import {
  BUSINESS_ENTITY_TYPES,
  CHARITABLE_ENTITY_TYPES,
  GRANTOR_ENTITY_TYPES,
  INSURANCE_POLICY_ENTITY_TYPES,
} from '@/modules/entities/entities.constants';
import { useUpdateEntitiesMutation } from '@/modules/entities/graphql/UpdateEntities.generated';
import {
  defaultPolicy,
  defaultValues as insuranceDefaultValues,
  Fields as InsuranceFields,
} from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { InsurancePolicyForm } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.UI';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import {
  createGroupedOptionsForTypeaheadSelect,
  groupEntitiesByCategory,
} from '@/modules/entities/utils/groupEntitiesByCategory';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useCreateLiabilityWithEntityUpdatesMutation } from '@/modules/liabilities/LiabilityModal/graphql/LiabilityModal.generated';
import { LiabilityForm } from '@/modules/liabilities/LiabilityModal/LiabilityForm';
import {
  defaultValues as liabilityDefaultValues,
  LiabilityFormShape,
} from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import { ROUTE_KEYS } from '@/navigation/constants';
import { COLORS } from '@/styles/tokens/colors';
import { EntityKind } from '@/types/schema';

import { useEstateHoldingsContext } from '../../contexts/estateHoldings.context';
import {
  EstatePage_EntityFragment,
  Operations,
} from '../../graphql/EstatePageData.generated';
import {
  AssetHoldingsFormShape,
  HOLDING_TYPE_OPTIONS,
  HoldingType,
  InsuranceHoldingFormShape,
} from './AddHoldingsFormModal.types';
import {
  shouldDisableEntityForHoldingType,
  useHandleAssetsSubmit,
  useHandleInsuranceSubmit,
  useHandleLiabilitySubmit,
} from './AddHoldingsFormModal.utils';
import { AssetHoldingForm, AssetHoldingFormRef } from './AssetHoldingsForm';

interface InsurancePolicyFormWrapperProps {
  householdId: string;
  entityId?: string;
}

// Wrapper component to provide the correct form context for InsurancePolicyForm
function InsurancePolicyFormWrapper({
  householdId,
  entityId,
}: InsurancePolicyFormWrapperProps) {
  // The InsurancePolicyForm component expects the form context to have the exact structure
  // that matches the Fields interface from InsurancePolicyDetailsSubform.types.ts
  // Since our parent form has additional fields, we need this wrapper to pass through
  // the form context correctly
  return (
    <InsurancePolicyForm
      policyIndex={0}
      householdId={householdId}
      entityId={entityId}
      variant="default"
      internalCardVariantOverride="filled-outline"
      remove={() => {}} // No-op for single policy context
      additionalItemRendererProps={{
        hideDeleteButton: true, // Don't show delete button in this context
      }}
    />
  );
}

const HOLDING_TYPE_LABELS_SUCCESS: Record<HoldingType, string> = {
  asset: 'Asset',
  liability: 'Liability',
  insurance: 'Insurance policy',
};

const HOLDING_TYPE_LABELS_ERROR: Record<HoldingType, string> = {
  asset: 'asset',
  liability: 'liability',
  insurance: 'insurance policy',
};

export interface AddHoldingsModalProps {
  holdingsGroup?: EstatePage_EntityFragment;
  open: boolean;
  onClose: () => void;
  /** The holding type to select when the owner does not require a specific type. */
  defaultHoldingType?: HoldingType;
}

export function AddHoldingsFormModal({
  holdingsGroup,
  open,
  onClose,
  defaultHoldingType = 'asset',
}: AddHoldingsModalProps) {
  const { entities } = useEstateHoldingsContext();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { householdId } = useHouseholdDetailsContext();
  const trackUserEvent = useTrackUserEvent();
  const { navigate } = useNavigateToRoute();

  const getTargetRouteKeyForEntityType = useCallback((entityType: string) => {
    if (
      BUSINESS_ENTITY_TYPES.includes(
        entityType as (typeof BUSINESS_ENTITY_TYPES)[number]
      )
    ) {
      return ROUTE_KEYS.HOUSEHOLD_ESTATE_BUSINESSES;
    } else if (
      [...GRANTOR_ENTITY_TYPES, ...CHARITABLE_ENTITY_TYPES].includes(
        entityType as
          | (typeof GRANTOR_ENTITY_TYPES)[number]
          | (typeof CHARITABLE_ENTITY_TYPES)[number]
      )
    ) {
      return ROUTE_KEYS.HOUSEHOLD_ESTATE_TRUSTS;
    } else {
      return ROUTE_KEYS.HOUSEHOLD_ESTATE_DIRECT_HOLDINGS;
    }
  }, []);

  // Form refs for each holding type
  const assetFormRef = useRef<AssetHoldingFormRef>(null);

  // Form instances for all form types
  const liabilityFormMethods = useForm<LiabilityFormShape>({
    defaultValues: {
      ...liabilityDefaultValues,
      balanceAsOfDate: new Date(), // Default to today's date for required field
    },
  });

  // Create a form that matches the Fields interface expected by InsurancePolicyForm
  const insuranceFormMethods = useForm<InsuranceFields>({
    defaultValues: {
      insurancePolicyDetailsSubform: {
        ...insuranceDefaultValues,
        policies: [
          {
            ...defaultPolicy,
          },
        ],
      },
    },
  });

  // Form instance for modal state management
  const modalFormMethods = useForm<{
    holdingsGroupId: string | null;
    holdingType: HoldingType;
  }>(
    {
      defaultValues: {
        holdingsGroupId: holdingsGroup?.id || null,
        holdingType: defaultHoldingType,
      },
    },
    'add-holdings-selection'
  );

  // Watch modal form values
  const selectedHoldingType = useWatch({
    control: modalFormMethods.control,
    name: 'holdingType',
  });
  const selectedHoldingsGroupId = useWatch({
    control: modalFormMethods.control,
    name: 'holdingsGroupId',
  });

  // Find the selected holdings group
  const selectedHoldingsGroup = useMemo(() => {
    if (!selectedHoldingsGroupId || !entities) return holdingsGroup;
    return entities.find((group) => group.id === selectedHoldingsGroupId);
  }, [selectedHoldingsGroupId, entities, holdingsGroup]);

  // Helper function to check if an entity should default to insurance type
  const shouldDefaultToInsuranceType = useCallback(
    (entity?: EstatePage_EntityFragment | null) => {
      if (!entity) return false;
      return (
        entity.kind === EntityKind.InsurancePersonalAccount ||
        entity.kind === EntityKind.IlitTrust
      );
    },
    []
  );

  // Reset owner-specific forms when the holdings group changes.
  useEffect(() => {
    if (!selectedHoldingsGroup) return;

    liabilityFormMethods.reset({
      ...liabilityDefaultValues,
      balanceAsOfDate: new Date(),
    });

    // Update the holding type to the default for this holdings group
    const newHoldingType = shouldDefaultToInsuranceType(selectedHoldingsGroup)
      ? 'insurance'
      : defaultHoldingType;

    modalFormMethods.setValue('holdingType', newHoldingType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHoldingsGroup, shouldDefaultToInsuranceType, defaultHoldingType]);

  // Check if the selected entity supports insurance policies
  const supportsInsurance = useMemo(() => {
    if (!selectedHoldingsGroup) return true;
    const entityType = getEntityTypeFromEntityKind(selectedHoldingsGroup.kind);
    return INSURANCE_POLICY_ENTITY_TYPES.includes(
      entityType as (typeof INSURANCE_POLICY_ENTITY_TYPES)[number]
    );
  }, [selectedHoldingsGroup]);

  const availableHoldingTypes = useMemo(() => {
    return HOLDING_TYPE_OPTIONS.map((option) => {
      // For InsurancePersonalAccount, only allow insurance type
      if (selectedHoldingsGroup?.kind === EntityKind.InsurancePersonalAccount) {
        if (option.value === 'asset' || option.value === 'liability') {
          return {
            ...option,
            disabled: true,
          };
        }
        return option;
      }

      if (!supportsInsurance && option.value === 'insurance') {
        return {
          ...option,
          disabled: true,
        };
      }

      return option;
    });
  }, [supportsInsurance, selectedHoldingsGroup]);

  const [
    upsertAssetToCurrentValuationWithEntityUpdates,
    { loading: upsertAssetLoading },
  ] = useUpsertAssetToCurrentValuationWithEntityUpdatesMutation({
    refetchQueries: [Operations.Query.EstatePageData],
    awaitRefetchQueries: true,
  });

  const [updateEntities, { loading: updateEntitiesLoading }] =
    useUpdateEntitiesMutation();

  const [
    createLiabilityWithEntityUpdates,
    { loading: createLiabilityLoading },
  ] = useCreateLiabilityWithEntityUpdatesMutation();

  const mutationLoading =
    upsertAssetLoading || updateEntitiesLoading || createLiabilityLoading;

  const handleClose = useCallback(() => {
    // Reset all forms to clear all fields when modal closes
    if (assetFormRef.current) {
      assetFormRef.current.reset();
    }

    liabilityFormMethods.reset({
      ...liabilityDefaultValues,
      balanceAsOfDate: new Date(),
    });

    insuranceFormMethods.reset({
      insurancePolicyDetailsSubform: {
        ...insuranceDefaultValues,
        policies: [
          {
            ...defaultPolicy,
          },
        ],
      },
    });

    // Reset modal form to default state
    modalFormMethods.reset({
      holdingsGroupId: holdingsGroup?.id || null,
      holdingType: shouldDefaultToInsuranceType(holdingsGroup)
        ? 'insurance'
        : defaultHoldingType,
    });

    onClose();
  }, [
    assetFormRef,
    liabilityFormMethods,
    insuranceFormMethods,
    modalFormMethods,
    holdingsGroup,
    onClose,
    shouldDefaultToInsuranceType,
    defaultHoldingType,
  ]);

  // Custom hooks for form submissions
  const handleAssetSubmitHook = useHandleAssetsSubmit({
    selectedHoldingsGroup,
    householdId,
    upsertAssetToCurrentValuationWithEntityUpdates,
    onCompleted: () => {
      showFeedback(
        `${HOLDING_TYPE_LABELS_SUCCESS[selectedHoldingType]} added successfully`,
        { variant: 'success' }
      );
      if (selectedHoldingsGroup && householdId) {
        const entityType = getEntityTypeFromEntityKind(
          selectedHoldingsGroup.kind
        );
        const targetRouteKey = getTargetRouteKeyForEntityType(entityType);
        navigate(targetRouteKey, { householdId });
      }
      handleClose();
    },
    onError: (err) => {
      showFeedback(
        `Failed to add ${HOLDING_TYPE_LABELS_ERROR[selectedHoldingType]}. Please try again.`
      );
      reportError(`Could not add direct holding ${selectedHoldingType}`, err);
    },
  });

  const handleInsuranceSubmitHook = useHandleInsuranceSubmit({
    selectedHoldingsGroup,
    householdId,
    updateEntities,
    onCompleted: () => {
      // Track the submission event
      if (selectedHoldingsGroup) {
        const entityType = getEntityTypeFromEntityKind(
          selectedHoldingsGroup.kind
        );
        // Count policies from the form
        const policies =
          insuranceFormMethods.getValues('insurancePolicyDetailsSubform')
            ?.policies || [];
        trackUserEvent('entity updated', {
          entityType,
          subform: 'insurance',
          policyCount: policies.length,
        });
      }

      showFeedback(
        `${HOLDING_TYPE_LABELS_SUCCESS[selectedHoldingType]} added successfully`,
        { variant: 'success' }
      );
      if (selectedHoldingsGroup && householdId) {
        const entityType = getEntityTypeFromEntityKind(
          selectedHoldingsGroup.kind
        );
        const targetRouteKey = getTargetRouteKeyForEntityType(entityType);
        navigate(targetRouteKey, { householdId });
      }
      handleClose();
    },
    onError: (err) => {
      showFeedback(
        `Failed to add ${HOLDING_TYPE_LABELS_ERROR[selectedHoldingType]}. Please try again.`
      );
      reportError(`Could not add direct holding ${selectedHoldingType}`, err);
    },
  });

  const handleLiabilitySubmitHook = useHandleLiabilitySubmit({
    entities: entities ?? [],
    householdId,
    createLiabilityWithEntityUpdates,
    onCompleted: () => {
      // Track the submission event
      const formData = liabilityFormMethods.getValues();
      trackUserEvent('liability created', {
        includesPaymentData: Boolean(formData.paymentKind),
      });

      showFeedback('Liability added successfully', { variant: 'success' });
      if (selectedHoldingsGroup && householdId) {
        const entityType = getEntityTypeFromEntityKind(
          selectedHoldingsGroup.kind
        );
        const targetRouteKey = getTargetRouteKeyForEntityType(entityType);
        navigate(targetRouteKey, { householdId });
      }
      handleClose();
    },
    onError: (err) => {
      showFeedback('Failed to add liability. Please try again.');
      reportError('Could not create liability', err);
    },
  });

  const holdingsGroupOptions = useMemo(() => {
    if (!entities) return [];

    // Group entities using the shared utility, but first apply disabled state based on holding type
    const entitiesWithDisabledState = entities.map((entity) => ({
      ...entity,
      disabled: shouldDisableEntityForHoldingType(
        entity.kind,
        selectedHoldingType
      ),
    }));

    const groupedEntities = groupEntitiesByCategory(entitiesWithDisabledState);

    return createGroupedOptionsForTypeaheadSelect(groupedEntities);
  }, [entities, selectedHoldingType]);

  const handleAssetSubmit = useCallback(
    async (data: AssetHoldingsFormShape) => {
      return handleAssetSubmitHook(data);
    },
    [handleAssetSubmitHook]
  );

  const handleLiabilitySubmit = useCallback(
    async (data: LiabilityFormShape) => {
      await handleLiabilitySubmitHook(data);
    },
    [handleLiabilitySubmitHook]
  );

  const handleInsuranceSubmit = useCallback(
    async (data: InsuranceFields) => {
      if (!selectedHoldingsGroup || !householdId) {
        return;
      }

      // Reconstruct the full form shape with modal data
      const fullFormData: InsuranceHoldingFormShape = {
        holdingsGroupId: selectedHoldingsGroup.id,
        holdingType: 'insurance',
        valuationAsOfDate: new Date(),
        insurancePolicyDetailsSubform: data.insurancePolicyDetailsSubform,
      };

      return handleInsuranceSubmitHook(fullFormData);
    },
    [selectedHoldingsGroup, householdId, handleInsuranceSubmitHook]
  );

  const handleFormSubmit = useCallback(() => {
    // For liabilities, skip modal form validation since Owner field is not required
    if (selectedHoldingType === 'liability') {
      void liabilityFormMethods.handleSubmit(handleLiabilitySubmit)();
      return;
    }

    // For other types, trigger validation on both forms simultaneously
    void modalFormMethods.handleSubmit(
      // Modal form is valid, now check individual holding form
      () => {
        if (selectedHoldingType === 'asset' && assetFormRef.current) {
          assetFormRef.current.submit();
        } else if (selectedHoldingType === 'insurance') {
          void insuranceFormMethods.handleSubmit(handleInsuranceSubmit)();
        }
      },
      // Modal form is invalid - errors are already shown
      () => {
        // Also trigger validation on holding form to show all errors at once
        if (selectedHoldingType === 'insurance') {
          void insuranceFormMethods.handleSubmit(
            () => {},
            () => {}
          )();
        }
      }
    )();
  }, [
    selectedHoldingType,
    liabilityFormMethods,
    handleLiabilitySubmit,
    modalFormMethods,
    insuranceFormMethods,
    handleInsuranceSubmit,
  ]);

  // Remove button disabling logic - validation now happens in handleFormSubmit

  return (
    <FormProvider {...modalFormMethods}>
      <FormModal
        heading="Add holdings"
        isOpen={open}
        onClose={handleClose}
        shouldBlockClose={mutationLoading}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClose}
              disabled={mutationLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleFormSubmit}
              disabled={mutationLoading}
              loading={mutationLoading}
            >
              Add holding
            </Button>
          </>
        }
      >
        <Stack spacing={2}>
          <Box sx={{ backgroundColor: COLORS.GRAY[100], padding: 2 }}>
            <FormAwareRadioGroup
              fieldName="holdingType"
              row
              hideLabel
              label="Holding type"
              options={availableHoldingTypes}
              control={modalFormMethods.control}
            />
          </Box>

          {selectedHoldingType !== 'liability' && (
            <FormAwareTypeaheadSelectInput
              fieldName="holdingsGroupId"
              label="Owner"
              placeholder="Search owners"
              options={holdingsGroupOptions}
              groupBy={(option) => option.groupName ?? ''}
              control={modalFormMethods.control}
              required
            />
          )}

          {selectedHoldingType === 'asset' && (
            <AssetHoldingForm
              ref={assetFormRef}
              holdingsGroup={selectedHoldingsGroup}
              onSubmit={handleAssetSubmit}
              disabled={mutationLoading}
            />
          )}

          {selectedHoldingType === 'liability' && (
            <FormProvider {...liabilityFormMethods}>
              <LiabilityForm entityId={selectedHoldingsGroup?.id} />
            </FormProvider>
          )}

          {selectedHoldingType === 'insurance' && householdId && (
            <FormProvider {...insuranceFormMethods}>
              <InsurancePolicyFormWrapper
                householdId={householdId}
                entityId={selectedHoldingsGroup?.id}
              />
            </FormProvider>
          )}
        </Stack>
      </FormModal>
    </FormProvider>
  );
}
